import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { CreditCard, Shield, CheckCircle, AlertCircle } from 'lucide-react';

const PaymentProcessor = ({ 
  amount, 
  orderDetails, 
  onSuccess, 
  onCancel,
  customerEmail = ''
}) => {
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvc: '',
    name: ''
  });

  const handleStripePayment = async () => {
    setProcessing(true);
    setError('');
    
    try {
      // Simulate Stripe payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Mock successful payment
      const paymentResult = {
        success: true,
        transactionId: `stripe_${Date.now()}`,
        amount: amount,
        method: 'stripe'
      };
      
      onSuccess(paymentResult);
    } catch (err) {
      setError('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayPalPayment = async () => {
    setProcessing(true);
    setError('');
    
    try {
      // Simulate PayPal redirect and payment
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const paymentResult = {
        success: true,
        transactionId: `paypal_${Date.now()}`,
        amount: amount,
        method: 'paypal'
      };
      
      onSuccess(paymentResult);
    } catch (err) {
      setError('PayPal payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = () => {
    if (paymentMethod === 'stripe') {
      handleStripePayment();
    } else {
      handlePayPalPayment();
    }
  };

  return (
    <div className="max-w-lg mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Secure Payment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Order Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Order Summary</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Package:</span>
                <span>{orderDetails?.package || 'Selected Package'}</span>
              </div>
              <div className="flex justify-between">
                <span>Setup Fee:</span>
                <span>{orderDetails?.setupFee || '£35.00'}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-1">
                <span>Total:</span>
                <span>£{amount}</span>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div>
            <Label>Payment Method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stripe">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Credit/Debit Card (Stripe)
                  </div>
                </SelectItem>
                <SelectItem value="paypal">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600 font-bold">PayPal</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stripe Card Form */}
          {paymentMethod === 'stripe' && (
            <div className="space-y-4">
              <div>
                <Label>Cardholder Name</Label>
                <Input
                  value={cardDetails.name}
                  onChange={(e) => setCardDetails({...cardDetails, name: e.target.value})}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Card Number</Label>
                <Input
                  value={cardDetails.number}
                  onChange={(e) => setCardDetails({...cardDetails, number: e.target.value})}
                  placeholder="1234 5678 9012 3456"
                  maxLength="19"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Expiry Date</Label>
                  <Input
                    value={cardDetails.expiry}
                    onChange={(e) => setCardDetails({...cardDetails, expiry: e.target.value})}
                    placeholder="MM/YY"
                    maxLength="5"
                  />
                </div>
                <div>
                  <Label>CVC</Label>
                  <Input
                    value={cardDetails.cvc}
                    onChange={(e) => setCardDetails({...cardDetails, cvc: e.target.value})}
                    placeholder="123"
                    maxLength="3"
                  />
                </div>
              </div>
            </div>
          )}

          {/* PayPal Info */}
          {paymentMethod === 'paypal' && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span className="font-medium">PayPal Checkout</span>
              </div>
              <p className="text-sm text-blue-700">
                You will be redirected to PayPal to complete your payment securely.
              </p>
            </div>
          )}

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={processing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              disabled={processing || (paymentMethod === 'stripe' && (!cardDetails.name || !cardDetails.number))}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              {processing ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </div>
              ) : (
                `Pay £${amount}`
              )}
            </Button>
          </div>

          {/* Security Notice */}
          <div className="text-center text-xs text-gray-500">
            <Shield className="h-3 w-3 inline mr-1" />
            Your payment is secured with 256-bit SSL encryption
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentProcessor;